import { useState } from "react";
import { MessageSquare, Send, Trash2, Pencil, Check, X } from "lucide-react";
import { cn } from "@/lib/cn";
import { avatarTone, formatDate } from "@/lib/format";
import { toast } from "sonner";
import { useComments, useAddComment, useUpdateComment, useDeleteComment, useFsComments, type FsComment } from "@/hooks/useComments";
import DOMPurify from "dompurify";
import { ExternalLink } from "lucide-react";
import { PersonPicker } from "@/components/ui/PersonPicker";
import { RichTextEditor } from "@/components/ui/RichTextEditor";
import { RichText } from "@/components/ui/RichText";

interface Props {
  projectId: string;
  apiPrefix?: string;
  /** Originating FS ticket id — when set, the mirrored Service Desk
   *  conversation interleaves with planner comments (read-only lane;
   *  Brett, 2026-08-28: one timeline, two unmistakable channels). */
  fsTicketId?: string | null;
  /** When true, drops the outer card chrome and "Comments" heading so
   *  the panel nests cleanly inside a parent that already provides its
   *  own header (e.g. CommentsDrawer). Default false — inline placements
   *  on Project / Initiative pages render the full card. */
  embedded?: boolean;
}

export function CommentsPanel({ projectId, apiPrefix = "/comments", embedded = false, fsTicketId }: Props) {
  const { data: comments, isLoading } = useComments(projectId, 50, apiPrefix);
  const { data: fsData } = useFsComments(projectId, !!fsTicketId);
  const fsComments = fsData?.comments ?? [];
  const addComment = useAddComment(projectId, apiPrefix);
  const updateComment = useUpdateComment(projectId, apiPrefix);
  const deleteComment = useDeleteComment(projectId, apiPrefix);
  const [author, setAuthor] = useState("");
  const [body, setBody] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  // Per-comment edit state. id == the comment being edited; draftBody
  // holds the in-flight markdown until saved or cancelled. One at a time —
  // starting an edit on another row cancels the first.
  const [editingId, setEditingId] = useState<number | null>(null);
  const [draftBody, setDraftBody] = useState("");

  const canSubmit = author.trim().length > 0 && body.trim().length > 0;

  const handleSubmit = () => {
    if (!canSubmit) return;
    addComment.mutate(
      { author: author.trim(), body: body.trim() },
      {
        onSuccess: () => {
          setBody("");
          toast.success("Comment added");
        },
        onError: (err) => toast.error((err as Error).message),
      },
    );
  };

  const handleStartEdit = (id: number, currentBody: string) => {
    setEditingId(id);
    setDraftBody(currentBody);
    setDeleteConfirm(null);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setDraftBody("");
  };

  const handleSaveEdit = (id: number) => {
    const trimmed = draftBody.trim();
    if (!trimmed) {
      toast.error("Comment body cannot be empty");
      return;
    }
    updateComment.mutate(
      { id, body: trimmed },
      {
        onSuccess: () => {
          setEditingId(null);
          setDraftBody("");
          toast.success("Comment updated");
        },
        onError: (err) => toast.error((err as Error).message),
      },
    );
  };

  const handleDelete = (id: number) => {
    deleteComment.mutate(id, {
      onSuccess: () => {
        setDeleteConfirm(null);
        toast.success("Comment deleted");
      },
    });
  };

  const list = comments ?? [];
  // One chronological timeline: planner comments + the mirrored Service
  // Desk thread, oldest first, each entry badged by channel.
  type Entry =
    | { kind: "planner"; ts: string; c: (typeof list)[number] }
    | { kind: "fs"; ts: string; f: FsComment };
  const timeline: Entry[] = [
    ...list.map((c) => ({ kind: "planner" as const, ts: c.created_at ?? "", c })),
    ...fsComments.map((f) => ({ kind: "fs" as const, ts: f.fs_created_at ?? "", f })),
  ].sort((a, b) => a.ts.localeCompare(b.ts));
  const total = timeline.length;

  return (
    <div className={cn(!embedded && "rounded-xl border border-line bg-surface p-6")}>
      {!embedded && (
        <div className="flex items-center gap-2 mb-4">
          <MessageSquare className="h-4 w-4 text-ink-muted" />
          <h2 className="text-sm font-semibold text-ink-2">
            Comments
            {total > 0 && (
              <span className="ml-1.5 rounded-full bg-raised px-1.5 py-0.5 text-[10px] font-medium text-ink-muted">
                {total}
              </span>
            )}
          </h2>
        </div>
      )}

      {/* Add comment form */}
      <div className="mb-5 rounded-lg border border-line bg-canvas/60 p-3">
        {fsTicketId && (
          <div className="mb-2 flex items-center gap-1.5 rounded-md bg-amber-50 dark:bg-amber-500/10 px-2 py-1.5 text-[10px] text-amber-800 dark:text-amber-300 ring-1 ring-amber-200 dark:ring-amber-500/30">
            <span>
              <strong>Internal planner comment</strong> — the requester will NOT see
              this. To reply to them, use
            </span>
            <a
              href={`https://helpdesk.etereman.com/a/tickets/${fsTicketId}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-0.5 font-semibold text-sky-700 dark:text-sky-300 hover:underline"
            >
              the ticket <ExternalLink className="h-2.5 w-2.5" />
            </a>
          </div>
        )}
        <div className="mb-2">
          <label className="mb-1 block text-[11px] font-medium text-ink-3">
            Your name <span className="text-rose-500" aria-hidden>*</span>
            <span className="sr-only">(required)</span>
          </label>
          <PersonPicker value={author} onChange={setAuthor} placeholder="Your name" />
        </div>
        <div className="mb-2">
          <RichTextEditor
            value={body}
            onChange={setBody}
            placeholder="Add a comment..."
            rows={3}
          />
        </div>
        <div className="flex justify-end">
          <button
            onClick={handleSubmit}
            disabled={!canSubmit || addComment.isPending}
            className="flex items-center gap-1.5 rounded-lg bg-brand-navy-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-navy-700 disabled:opacity-40 transition-colors"
          >
            <Send className="h-3 w-3" />
            {addComment.isPending ? "Posting..." : "Post"}
          </button>
        </div>
      </div>

      {/* Comments list */}
      {isLoading && (
        <div className="text-xs text-ink-subtle text-center py-4">Loading comments...</div>
      )}

      {!isLoading && total === 0 && (
        <div className="text-xs text-ink-subtle text-center py-4">
          No comments yet. Be the first to add one.
        </div>
      )}

      <div className="space-y-3">
        {timeline.map((entry) => {
          if (entry.kind === "fs") {
            const f = entry.f;
            return (
              <div key={`fs-${f.fs_conversation_id}`} className="flex gap-3">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-sky-100 dark:bg-sky-500/15 text-[10px] font-bold text-sky-700 dark:text-sky-300">
                  SD
                </div>
                <div className={cn(
                  "min-w-0 flex-1 rounded-lg border px-3 py-2",
                  f.is_private ? "border-amber-200 dark:border-amber-500/30 bg-amber-50/60 dark:bg-amber-500/10" : "border-sky-100 dark:border-sky-500/30 bg-sky-50/40 dark:bg-sky-500/10",
                )}>
                  <div className="flex items-baseline gap-2">
                    <span className="text-xs font-semibold text-ink">{f.author || "Unknown"}</span>
                    <span className="rounded-full bg-sky-100 dark:bg-sky-500/15 px-1.5 py-0.5 text-[8px] font-semibold uppercase text-sky-700 dark:text-sky-300">
                      Service Desk
                    </span>
                    {f.is_private && (
                      <span className="rounded-full bg-amber-200 px-1.5 py-0.5 text-[8px] font-semibold uppercase text-amber-900 dark:text-amber-300">
                        internal note
                      </span>
                    )}
                    <span className="text-[10px] text-ink-subtle">
                      {formatDate(f.fs_created_at?.split("T")[0] ?? "")}
                    </span>
                  </div>
                  <div
                    className="fs-rich-text mt-1 text-xs leading-relaxed text-ink-2"
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(f.body_html ?? "", { FORBID_TAGS: ["script", "style", "iframe"] }),
                    }}
                  />
                </div>
              </div>
            );
          }
          const c = entry.c;
          const initials = c.author
            .split(" ")
            .map((n) => n[0])
            .join("")
            .slice(0, 2)
            .toUpperCase();

          return (
            <div key={c.id} className="flex gap-3">
              <div
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[10px] font-bold text-ink-2"
                style={{ backgroundColor: avatarTone(c.author) }}
              >
                {initials}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline gap-2">
                  <span className="text-xs font-semibold text-ink">{c.author}</span>
                  <span className="text-[10px] text-ink-subtle">
                    {formatDate(c.created_at?.split("T")[0] ?? c.created_at?.split(" ")[0])}
                  </span>
                  {c.comment_type === "status_update" && (
                    <span className="rounded-full bg-sky-100 dark:bg-sky-500/15 px-1.5 py-0.5 text-[8px] font-semibold uppercase text-sky-700 dark:text-sky-300">
                      Status Update
                    </span>
                  )}
                </div>
                {editingId === c.id ? (
                  <div className="mt-1">
                    <RichTextEditor
                      value={draftBody}
                      onChange={setDraftBody}
                      rows={3}
                      placeholder="Edit comment..."
                    />
                    <div className="mt-1 flex items-center gap-2">
                      <button
                        onClick={() => handleSaveEdit(c.id)}
                        disabled={updateComment.isPending || !draftBody.trim()}
                        className="flex items-center gap-1 rounded-md bg-brand-navy-600 px-2 py-1 text-[10px] font-semibold text-white hover:bg-brand-navy-700 disabled:opacity-40 transition-colors"
                      >
                        <Check className="h-3 w-3" />
                        {updateComment.isPending ? "Saving..." : "Save"}
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        className="flex items-center gap-1 rounded-md border border-line bg-surface px-2 py-1 text-[10px] font-medium text-ink-3 hover:bg-raised transition-colors"
                      >
                        <X className="h-3 w-3" /> Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <RichText text={c.body} className="mt-1 text-xs text-ink-2" />
                    {c.updated_at && c.created_at && c.updated_at !== c.created_at && (
                      <div className="mt-0.5 text-[9px] italic text-ink-subtle">
                        edited {formatDate(c.updated_at?.split("T")[0] ?? c.updated_at?.split(" ")[0])}
                      </div>
                    )}
                  </>
                )}
                {editingId !== c.id && (
                  <div className="mt-1">
                    {deleteConfirm === c.id ? (
                      <div className="flex items-center gap-2 text-[10px]">
                        <span className="text-red-500">Delete this comment?</span>
                        <button
                          onClick={() => handleDelete(c.id)}
                          className="font-semibold text-red-600 hover:text-red-800 dark:hover:text-red-300"
                        >
                          Yes
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(null)}
                          className="text-ink-subtle hover:text-ink-3"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleStartEdit(c.id, c.body)}
                          aria-label="Edit comment"
                          className="text-[10px] text-ink-faint hover:text-brand-navy-500 transition-colors"
                        >
                          <Pencil className="h-3 w-3" />
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(c.id)}
                          aria-label="Delete comment"
                          className="text-[10px] text-ink-faint hover:text-red-500 transition-colors"
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
